import { translate, useTranslation } from '@rhino-project/core/i18n';
import { ListBoxItem } from '@heroui/react';
import {
  useModelAndAttributeFromPath,
  useModelContext
} from '@rhino-project/core/hooks';
import { getModelAndAttributeFromPath } from '@rhino-project/core/utils';
import { compact } from 'lodash-es';
import { ReactNode, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  DisplayBooleanProps,
  DisplayDateTimeProps,
  DisplayImageProps,
  DisplayLinkProps,
  DisplayTimeProps
} from './Display';
import { DisplayInputProps } from './DisplayInput';
import { DisplayTextareaProps } from './DisplayTextarea';
import { FieldInputProps } from './FieldInput';
import { FieldTextareaProps } from './FieldTextarea';
import { FieldBooleanProps, FieldFileProps, FieldTimeProps } from './Field';
import { FieldDatePickerProps } from './FieldDatePicker';
import { FieldSelectProps } from './FieldSelect';
import { ModelFieldReferenceProps } from './ModelField';
import { DisplayNumberInputProps } from './DisplayNumberInput';
import { FieldNumberInputProps } from './FieldNumberInput';
import { RhinoAttribute, RhinoResource } from '@rhino-project/core';

export const useModelFieldLabel = ({ path }: { path: string }) => {
  const { model } = useModelContext() as { model: RhinoResource };
  const { attribute } = useModelAndAttributeFromPath(model, path) as {
    attribute: { readableName: string };
  };

  return attribute.readableName;
};

export const useModelFieldClearable = ({ path }: { path: string }) => {
  const { model } = useModelContext() as { model: RhinoResource };
  const { attribute } = useModelAndAttributeFromPath(model, path) as {
    attribute: { nullable: string | undefined };
  };

  return !!attribute.nullable;
};

export const useModelFieldRequired = ({ path }: { path: string }) => {
  const { model } = useModelContext() as { model: RhinoResource };
  const { attribute } = useModelAndAttributeFromPath(model, path) as {
    attribute: { required?: boolean };
  };

  return !!attribute.required;
};

export const useModelFieldInputProps = <
  T extends FieldInputProps | FieldTextareaProps
>(
  props: T
): T => {
  const { path } = props;
  const { setValue } = useFormContext();
  const label = useModelFieldLabel(props);
  const isClearable = useModelFieldClearable(props);
  const isRequired = useModelFieldRequired(props);
  const onClear = useCallback(() => setValue(path, null), [path, setValue]);

  return { label, isClearable, isRequired, onClear, ...props };
};

export const useModelFieldNumberInputProps = <T extends FieldNumberInputProps>(
  props: T
): T => {
  const { path } = props;
  const { setValue } = useFormContext();
  const label = useModelFieldLabel(props);
  const isClearable = useModelFieldClearable(props);
  const isRequired = useModelFieldRequired(props);
  const onClear = useCallback(() => setValue(path, null), [path, setValue]);

  return { label, isClearable, isRequired, onClear, ...props };
};

export const useModelFieldBooleanProps = <T extends FieldBooleanProps>(
  props: T
): T => {
  const children = useModelFieldLabel(props);
  const isRequired = useModelFieldRequired(props);

  return { children, isRequired, ...props };
};

export const useModelFieldCountryProps = <T extends FieldSelectProps>(
  props: T
): T => {
  const { t } = useTranslation();
  const { path } = props;
  const { setValue } = useFormContext();
  const { model } = useModelContext() as { model: RhinoResource };
  const { attribute } = useModelAndAttributeFromPath(model, path) as {
    attribute: { readableName: string };
  };
  const label = useModelFieldLabel(props);
  const placeholder = t('rhino.frontend.messages.select_a_value', {
    value1: attribute.readableName.toLowerCase()
  });
  const isClearable = useModelFieldClearable(props);
  const isRequired = useModelFieldRequired(props);
  const onClear = useCallback(() => setValue(path, null), [path, setValue]);

  return { label, placeholder, isClearable, isRequired, onClear, ...props };
};

export const useModelFieldDateTimeProps = <T extends FieldDatePickerProps>(
  props: T
): T => {
  const label = useModelFieldLabel(props);
  const isRequired = useModelFieldRequired(props);

  return { label, isRequired, ...props };
};

export const useModelFieldEnumProps = ({
  children: propsChildren,
  ...props
}: FieldSelectProps): FieldSelectProps => {
  const { t } = useTranslation();
  const { path } = props;
  const label = useModelFieldLabel(props);
  const isRequired = useModelFieldRequired(props);
  const { model } = useModelContext() as { model: RhinoResource };
  const { attribute } = useModelAndAttributeFromPath(model, path) as {
    attribute: {
      enum: string[];
      readableName: string;
      enumLabels?: Record<string, string>;
    };
  };

  const children = useMemo(() => {
    // children can be a single element or an array
    if (propsChildren)
      return Array.isArray(propsChildren) ? propsChildren : [propsChildren];

    // Humanize underscored enum values (e.g. "in_progress" → "In Progress").
    // Title case in JS so both the dropdown items and the SelectValue trigger
    // display the readable label (CSS capitalize only applies to the ListBoxItem).
    return attribute.enum.map((e) => {
      const label =
        attribute.enumLabels?.[e] ||
        e.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
      return (
        <ListBoxItem key={e} id={e} textValue={label}>
          {label}
        </ListBoxItem>
      );
    });
  }, [attribute.enum, attribute.enumLabels, propsChildren]);

  const accessor = useCallback(
    (value: unknown): string | null => (value as string) || null,
    []
  );

  const placeholder = t('rhino.frontend.messages.select_a_value', {
    value1: attribute.readableName.toLowerCase()
  });

  return { accessor, children, label, placeholder, isRequired, ...props };
};

export const useModelFieldIntegerSelectProps = ({
  children: propsChildren,
  ...props
}: FieldSelectProps): FieldSelectProps => {
  const { t } = useTranslation();
  const { path } = props;
  const label = useModelFieldLabel(props);
  const isRequired = useModelFieldRequired(props);
  const { model } = useModelContext() as { model: RhinoResource };
  const {
    attribute: { minimum, maximum, readableName }
  } = useModelAndAttributeFromPath(model, path) as {
    attribute: { minimum: number; maximum: number; readableName: string };
  };

  const children = useMemo(() => {
    // children can be a single element or an array
    if (propsChildren)
      return Array.isArray(propsChildren) ? propsChildren : [propsChildren];

    return Array.from({ length: maximum - minimum }, (_, i) => (
      <ListBoxItem
        key={String(i + minimum)}
        id={String(i + minimum)}
        textValue={String(i + minimum)}
      >
        {i + minimum}
      </ListBoxItem>
    ));
  }, [maximum, minimum, propsChildren]);

  const accessor = useCallback(
    (value: unknown) => (value ? String(value) : null),
    []
  );

  const placeholder = t('rhino.frontend.messages.select_a_value', {
    value1: readableName.toLowerCase()
  });

  return { accessor, children, label, placeholder, isRequired, ...props };
};

export const useModelFieldFileProps = <
  T extends Omit<FieldFileProps, 'label'> &
    Partial<Pick<FieldFileProps, 'label'>>
>(
  props: T
): T => {
  const label = useModelFieldLabel(props);
  const isRequired = useModelFieldRequired(props);

  return { label, isRequired, ...props };
};

export const useModelFieldReferenceProps = <T extends ModelFieldReferenceProps>(
  props: T
): T => {
  const label = useModelFieldLabel(props);
  const isClearable = useModelFieldClearable(props);
  const isRequired = useModelFieldRequired(props);

  return { label, isClearable, isRequired, ...props };
};

export const useModelFieldTimeProps = <T extends FieldTimeProps>(
  props: T
): T => {
  const label = useModelFieldLabel(props);
  const isRequired = useModelFieldRequired(props);

  return { label, isRequired, ...props };
};

export const useModelDisplayLabel = ({ path }: { path: string }) => {
  const { model } = useModelContext() as { model: RhinoResource };
  const { attribute } = useModelAndAttributeFromPath(model, path) as {
    attribute: { readableName: string };
  };

  return attribute.readableName;
};

export const useModelDisplayInputProps = <
  T extends DisplayInputProps | DisplayTextareaProps
>(
  props: T
): T => {
  const { path } = props;
  const label = useModelDisplayLabel({ path });

  return { label, ...props };
};

export const useModelDisplayNumberInputProps = <
  T extends DisplayNumberInputProps
>(
  props: T
): T => {
  const { path } = props;
  const label = useModelDisplayLabel({ path });

  return { label, ...props };
};

export const useModelDisplayBooleanProps = <T extends DisplayBooleanProps>(
  props: T
): T => {
  const { path } = props;
  const children = useModelDisplayLabel({ path });

  return { children, ...props };
};

export const useModelDisplayDateTimeProps = <T extends DisplayDateTimeProps>(
  props: T
): T => {
  const { path } = props;
  const label = useModelDisplayLabel({ path });

  return { label, ...props };
};

export const useModelDisplayTimeProps = <T extends DisplayTimeProps>(
  props: T
): T => {
  const { path } = props;
  const label = useModelDisplayLabel({ path });

  return { label, ...props };
};

export const useModelDisplayAttachmentProps = (
  props: DisplayLinkProps
): DisplayLinkProps => {
  const accessor = useCallback((value: unknown): string | null | undefined => {
    const typedValue = value as { url?: string } | null | undefined;
    return typedValue?.url;
  }, []);

  return {
    accessor,
    ...props
  };
};

export const useModelDisplayAttachmentImageProps = (
  props: DisplayImageProps
): DisplayImageProps => {
  const { path } = props;

  const accessor = useCallback((value: unknown): string | null | undefined => {
    const typedValue = value as { url?: string } | null | undefined;
    return typedValue?.url;
  }, []);
  const watch = useWatch<{
    [key: string]: { display_name?: string; url?: string };
  }>({ name: path });

  return {
    accessor,
    alt: watch?.display_name,
    ...props
  };
};

export const useFilterField = (path: string, operator: string) => {
  const operatorPath = useMemo(
    () => compact([path, operator]).join('.'),
    [path, operator]
  );

  return { operatorPath };
};

const isDateRelated = (format: string) =>
  ['date', 'time', 'datetime'].includes(format);

const operatorToDateLabel = (operator: string) => {
  switch (operator) {
    case 'diff':
      return translate('rhino.frontend.messages.not');
    case 'gt':
    case 'gteq':
      return translate('rhino.frontend.messages.after');
    case 'lt':
    case 'lteq':
      return translate('rhino.frontend.messages.before');
    default:
      return '';
  }
};

export const dateFilterOperatorLabel = (format: string, operator: string) =>
  isDateRelated(format) ? operatorToDateLabel(operator) : '';

const operatorToLabel = (format: string, operator: string) => {
  if (isDateRelated(format)) return operatorToDateLabel(operator);

  switch (operator) {
    case 'diff':
      return '≠';
    case 'gt':
      return '>';
    case 'gteq':
      return '>=';
    case 'lt':
      return '<';
    case 'lteq':
      return '<=';
    default:
      return '';
  }
};

// Builds self-describing pill text so an active filter is identifiable
// without the form visible, e.g. "Grad Year: 2026" or "Dob Year >= 2008".
// Date/time filters format their values and build their own pill text.
export const filterPillText = (
  attribute: RhinoAttribute,
  operator: string | undefined | null,
  value: unknown
) => {
  if (value == null || value === '') return null;

  const operatorLabel = operator
    ? operatorToLabel(attribute.format as string, operator)
    : '';

  if (operatorLabel)
    return `${attribute.readableName} ${operatorLabel} ${value}`;

  return `${attribute.readableName}: ${value}`;
};

export const useModelFilterField = (
  path: string,
  options: { label?: ReactNode } = {}
) => {
  const { model } = useModelContext() as { model: RhinoResource };

  const { t } = useTranslation();
  const [attributeModel, originalAttribute, operator, plainPath] = useMemo(
    () => getModelAndAttributeFromPath(model, path),
    [model, path]
  ) as [RhinoResource, RhinoAttribute, string, string];

  const attribute = useMemo(
    () => ({
      ...originalAttribute,
      readableName: t(
        `rhino.generated.${attributeModel.model}.attributes.${originalAttribute.name}`,
        { defaultValue: originalAttribute.readableName }
      )
    }),
    [originalAttribute, attributeModel, t]
  );
  const filterField = useFilterField(plainPath, operator);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const label = useMemo(() => {
    if (options?.label) return options.label;

    const operatorLabel = operatorToLabel(attribute.format as string, operator);

    if (operatorLabel) return `${attribute.readableName} ${operatorLabel}`;

    return attribute.readableName;
  }, [attribute, operator, options?.label]);

  // FIXME: Memoize this?
  return {
    attribute,
    attributeModel,
    label,
    operator,
    plainPath,
    ...filterField
  };
};
